// ─────────────────────────────────────────────────────────────────────────────
// cvSlice.ts — Redux slice for all CV data
//
// Uses Redux Toolkit's createAsyncThunk to fetch CV data from the active
// back-end API, and createSlice to define reducers + actions.
// RTK uses Immer under the hood, so mutations inside reducers are safe.
// ─────────────────────────────────────────────────────────────────────────────

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { CVData, LoadingState } from '@/types/cv.types';
import { cvService } from '@/services/cvService';

// ── State Shape ───────────────────────────────────────────────────────────────
interface CVState {
  data: CVData | null;      // null until the first successful fetch
  status: LoadingState;
  error: string | null;
  lastFetched: number | null; // Unix timestamp — used to avoid re-fetching
}

const initialState: CVState = {
  data: null,
  status: 'idle',
  error: null,
  lastFetched: null,
};

// ── Async Thunks ──────────────────────────────────────────────────────────────

/**
 * fetchCV — Loads CV data from the active back-end.
 * The service layer (cvService) resolves the correct API base URL based on
 * which BackEndArchitecture is currently selected in the UI slice.
 */
export const fetchCV = createAsyncThunk<CVData, void, { rejectValue: string }>(
  'cv/fetchCV',
  async (_, { rejectWithValue }) => {
    try {
      return await cvService.getCV();
    } catch (err) {
      // Return a serialisable error message so the slice can store it
      const message = err instanceof Error ? err.message : 'Failed to fetch CV data';
      return rejectWithValue(message);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const cvSlice = createSlice({
  name: 'cv',
  initialState,

  reducers: {
    /**
     * resetCV — Clears fetched data (e.g. when switching back-ends so
     * the component knows to re-fetch from the new API endpoint).
     */
    resetCV: (state) => {
      state.data = null;
      state.status = 'idle';
      state.error = null;
      state.lastFetched = null;
    },

    /**
     * updatePersonalInfo — Allows optimistic local edits to personal info
     * without a round-trip. Useful for a future "edit mode" feature.
     */
    updatePersonalInfo: (state, action: PayloadAction<Partial<CVData['personal']>>) => {
      if (state.data) {
        state.data.personal = { ...state.data.personal, ...action.payload };
      }
    },
  },

  // extraReducers handles the lifecycle actions generated by createAsyncThunk
  extraReducers: (builder) => {
    builder
      // Fetch started — show skeleton loaders in the UI
      .addCase(fetchCV.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      // Fetch succeeded — store the data
      .addCase(fetchCV.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        state.lastFetched = Date.now();
      })
      // Fetch failed — surface the error message
      .addCase(fetchCV.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const { resetCV, updatePersonalInfo } = cvSlice.actions;
export default cvSlice.reducer;
